import { type UIMessage, useChat } from "@ai-sdk/react";
import { ArrowDownIcon } from "@phosphor-icons/react";
import { getRouteApi } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { useClient } from "urql";
import ChatEmptyState from "@/components/ui/chat/ChatEmptyState";
import ChatErrorBoundary, {
  ChatErrorBanner,
} from "@/components/ui/chat/ChatErrorBoundary";
import ChatInput, {
  appendDictatedPhrase,
  type ChatInputProps,
  notifyDictationTruncated,
} from "@/components/ui/chat/ChatInput";
import { ChatPromptSubmitContext } from "@/components/ui/chat/ChatPromptSubmitContext";
import IconButton from "@/components/ui/icon-button";
import { stopConversationStream } from "@/data-access/herald-chat/chat-http";
import { GetConversationMessages } from "@/data-access/herald-chat/chat-queries";
import useChatScroll from "@/lib/hooks/chat/useChatScroll";
import useChatSubmit, {
  type SendableMessage,
} from "@/lib/hooks/chat/useChatSubmit";
import useChatTiming from "@/lib/hooks/chat/useChatTiming";
import useChatTransport, {
  type ConversationStreamFailure,
} from "@/lib/hooks/chat/useChatTransport";
import { useHeraldChatInstanceRemountEffect } from "@/lib/hooks/chat/useHeraldChatInstanceRemountEffect";
import { useHeraldChatSidebarSync } from "@/lib/hooks/chat/useHeraldChatSidebarSync";
import useRetrievePreviousConversation, {
  CONVERSATION_MESSAGES_PAGE_SIZE,
  mapConversationMessageResultsToUiMessages,
  mergeUiMessagesWithServerSnapshot,
  reconcileInactiveResumeWithServerSnapshot,
  resetActiveAssistantMessage,
} from "@/lib/hooks/chat/useRetrievePreviousConversation";
import { useStreamingQuipForLatestAssistant } from "@/lib/hooks/chat/useStreamingQuipForLatestAssistant";
import useVoiceToText, {
  type VoiceRecognitionErrorInfo,
  voiceRecognitionToastMessage,
} from "@/lib/hooks/chat/useVoiceToText";
import useHeraldChatHistoryStore from "@/store/herald-chat/herald-chat-history.slice";
import useHeraldChatSettingsStore from "@/store/herald-chat/herald-chat-settings.slice";
import { useHeraldDebugToolCallsEnabled } from "./dev-tools/heraldDebugToolCalls";
import { HeraldChatMessageList } from "./HeraldChatMessageList";

const EMPTY_UI_MESSAGES: UIMessage[] = [];

/** Backoff for reconnecting a dropped stream: 500 ms, 1 s, 2 s, 4 s, 8 s, 10 s, then the error banner. */
const STREAM_RECONNECT_BASE_DELAY_MS = 500;
const STREAM_RECONNECT_MAX_DELAY_MS = 10_000;
const STREAM_RECONNECT_MAX_ATTEMPTS = 6;

const readMessageText = (message: UIMessage): string =>
  message.parts.map((part) => (part.type === "text" ? part.text : "")).join("");

const heraldChatRouteApi = getRouteApi("/_authenticated/chat/$conversationId");

const HeraldChat = () => {
  const { conversationId: conversationIdParam } =
    heraldChatRouteApi.useParams();
  const conversationId =
    conversationIdParam === "new" ? undefined : conversationIdParam;

  return (
    <HeraldChatSession key="herald-chat-pane" conversationId={conversationId} />
  );
};

const HeraldChatSession = ({ conversationId }: { conversationId?: string }) => {
  const skipMessagesFetchForConversationId = useHeraldChatHistoryStore(
    (state) => state.skipMessagesFetchForConversationId,
  );
  const clearSkipMessagesFetchUnlessViewing = useHeraldChatHistoryStore(
    (state) => state.clearSkipMessagesFetchUnlessViewing,
  );

  useEffect(() => {
    clearSkipMessagesFetchUnlessViewing(conversationId);
  }, [conversationId, clearSkipMessagesFetchUnlessViewing]);

  const { messages: loadedMessages, isLoading } =
    useRetrievePreviousConversation(conversationId);

  const initialMessages = useMemo(
    () => loadedMessages ?? EMPTY_UI_MESSAGES,
    [loadedMessages],
  );

  const skipHistoryFetch =
    conversationId !== undefined &&
    skipMessagesFetchForConversationId === conversationId;

  const showLoadingGate = Boolean(
    conversationId && isLoading && !skipHistoryFetch,
  );

  const setIsHeraldChatEmpty = useHeraldChatSettingsStore(
    (state) => state.setIsHeraldChatEmpty,
  );

  // useLayoutEffect runs before paint so empty-state chrome (e.g. gradient) does not flash for one frame while the loading gate is shown.
  useLayoutEffect(() => {
    if (showLoadingGate) {
      setIsHeraldChatEmpty(false);
    }
  }, [showLoadingGate]);

  if (showLoadingGate) {
    return (
      <div className="flex min-w-0 flex-1 items-center justify-center">
        <span className="text-sm text-gray-400">Loading conversation...</span>
      </div>
    );
  }

  return (
    <HeraldChatInner
      conversationId={conversationId}
      initialMessages={initialMessages}
    />
  );
};

const HeraldChatInner = ({
  conversationId,
  initialMessages,
}: {
  conversationId?: string;
  initialMessages: UIMessage[];
}) => {
  const {
    transport,
    conversationId: capturedConversationId,
    getTransportConversationId,
    getTransportConversationTitle,
    getTransportAssistantMessageId,
    setConversationResumeAcceptedHandler,
    getConversationResumeResult,
    getConversationStreamFailure,
    getConversationStreamLastEventId,
    disconnectConversationStream,
    resetConversation,
    setConversationId,
  } = useChatTransport({ conversationId });

  const urqlClient = useClient();

  const registerConversationFromFirstStream = useHeraldChatHistoryStore(
    (state) => state.registerConversationFromFirstStream,
  );
  const optimisticallyUpsertConversationAtTop = useHeraldChatHistoryStore(
    (state) => state.optimisticallyUpsertConversationAtTop,
  );

  const [chatInstanceId, setChatInstanceId] = useState(
    () => `herald-${crypto.randomUUID()}`,
  );
  const [activeResumeAttemptKey, setActiveResumeAttemptKey] = useState<
    string | null
  >(null);
  const resumeAttemptKey = `${chatInstanceId}:${conversationId ?? ""}`;
  const isActiveResumeAccepted =
    conversationId != null && activeResumeAttemptKey === resumeAttemptKey;

  const {
    messages,
    sendMessage,
    setMessages,
    status,
    stop,
    error,
    regenerate,
    clearError,
    resumeStream,
  } = useChat({
    id: chatInstanceId,
    transport,
    messages: initialMessages,
    throttle: 100,
  });

  const newlyCreatedConversationIdRef = useRef<string | null>(null);
  const resumeAttemptKeyRef = useRef<string | null>(null);
  const resumedAssistantMessageIdRef = useRef<string | null>(null);
  const stopRef = useRef(stop);
  const statusRef = useRef(status);
  stopRef.current = stop;
  statusRef.current = status;

  /** Replaces local rows with saved server history (after a 204 resume or a 409) unless a new turn has started. */
  const reconcileConversationWithServerHistory = useCallback(
    async (historyConversationId: string): Promise<void> => {
      const result = await urqlClient
        .query(
          GetConversationMessages,
          {
            filters: { conversationId: historyConversationId },
            pagination: { limit: CONVERSATION_MESSAGES_PAGE_SIZE },
          },
          { requestPolicy: "network-only" },
        )
        .toPromise();
      const rawMessages = result.data?.conversationMessages?.results;
      if (
        rawMessages == null ||
        statusRef.current === "submitted" ||
        statusRef.current === "streaming"
      ) {
        return;
      }
      const serverMessages =
        mapConversationMessageResultsToUiMessages(rawMessages);
      setMessages((currentMessages) =>
        reconcileInactiveResumeWithServerSnapshot(
          currentMessages,
          serverMessages,
        ),
      );
    },
    [setMessages, urqlClient],
  );

  useEffect(() => {
    if (!conversationId) {
      newlyCreatedConversationIdRef.current = capturedConversationId;
      return;
    }

    if (conversationId === newlyCreatedConversationIdRef.current) {
      newlyCreatedConversationIdRef.current = null;
      return;
    }
    newlyCreatedConversationIdRef.current = null;

    if (resumeAttemptKey === resumeAttemptKeyRef.current) {
      return;
    }

    resumeAttemptKeyRef.current = resumeAttemptKey;
    resumedAssistantMessageIdRef.current = null;
    setConversationResumeAcceptedHandler((assistantMessageId) => {
      resumedAssistantMessageIdRef.current = assistantMessageId;
      setActiveResumeAttemptKey(resumeAttemptKey);
      setMessages((currentMessages) =>
        resetActiveAssistantMessage(currentMessages, assistantMessageId),
      );
    });

    void (async () => {
      try {
        await resumeStream();
        if (getConversationResumeResult() === "inactive") {
          await reconcileConversationWithServerHistory(conversationId);
        }
      } finally {
        setActiveResumeAttemptKey((currentAttemptKey) =>
          currentAttemptKey === resumeAttemptKey ? null : currentAttemptKey,
        );
      }
    })();
  }, [
    capturedConversationId,
    chatInstanceId,
    conversationId,
    getConversationResumeResult,
    reconcileConversationWithServerHistory,
    resumeAttemptKey,
    resumeStream,
    setConversationResumeAcceptedHandler,
    setMessages,
  ]);

  useEffect(
    () => () => disconnectConversationStream(),
    [conversationId, disconnectConversationStream],
  );

  useEffect(() => () => void stopRef.current(), []);

  // A dropped stream is a disconnect, not a cancel: the server turn keeps running, so reconnect to it.
  const [streamReconnectReason, setStreamReconnectReason] =
    useState<ConversationStreamFailure | null>(null);
  const isStreamReconnecting = streamReconnectReason != null;
  const streamReconnectAttemptRef = useRef(0);
  const streamReconnectLastEventIdRef = useRef<string | null>(null);
  const streamReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastChatRequestTriggerRef = useRef<"submit" | "regenerate">("submit");
  const handledStreamErrorRef = useRef<Error | null>(null);

  const cancelStreamReconnect = useCallback(() => {
    if (streamReconnectTimerRef.current != null) {
      clearTimeout(streamReconnectTimerRef.current);
      streamReconnectTimerRef.current = null;
    }
    streamReconnectAttemptRef.current = 0;
    setStreamReconnectReason(null);
  }, []);

  useEffect(
    () => cancelStreamReconnect,
    [conversationId, cancelStreamReconnect],
  );

  const reconnectConversationStream = useCallback(async () => {
    streamReconnectTimerRef.current = null;
    await resumeStream();
    if (getConversationStreamFailure() === "stream_interrupted") {
      // The stream-error effect schedules the next attempt.
      return;
    }
    setStreamReconnectReason(null);
    const inactiveConversationId =
      conversationId ?? getTransportConversationId();
    if (
      getConversationResumeResult() === "inactive" &&
      inactiveConversationId != null
    ) {
      await reconcileConversationWithServerHistory(inactiveConversationId);
    }
  }, [
    conversationId,
    getConversationResumeResult,
    getConversationStreamFailure,
    getTransportConversationId,
    reconcileConversationWithServerHistory,
    resumeStream,
  ]);

  const messagesOnVisibleRef = useRef({
    setMessages,
    conversationId,
    getTransportConversationId,
    client: urqlClient,
    isStreaming: false,
  });
  messagesOnVisibleRef.current = {
    setMessages,
    conversationId,
    getTransportConversationId,
    client: urqlClient,
    isStreaming:
      status === "streaming" || status === "submitted" || isStreamReconnecting,
  };
  useEffect(() => {
    let debounceId: ReturnType<typeof setTimeout> | undefined;
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (debounceId !== undefined) clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        debounceId = undefined;
        void (async () => {
          const r = messagesOnVisibleRef.current;
          const convId =
            r.conversationId ?? r.getTransportConversationId() ?? null;
          if (!convId || r.isStreaming) return;
          try {
            const out = await r.client
              .query(
                GetConversationMessages,
                {
                  filters: { conversationId: convId },
                  pagination: { limit: CONVERSATION_MESSAGES_PAGE_SIZE },
                },
                { requestPolicy: "network-only" },
              )
              .toPromise();
            const raw = out.data?.conversationMessages?.results;
            if (raw == null || raw.length === 0) return;
            const fresh = mapConversationMessageResultsToUiMessages(raw);
            r.setMessages((prev) =>
              mergeUiMessagesWithServerSnapshot(prev, fresh),
            );
          } catch {
            return;
          }
        })();
      }, 300);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (debounceId !== undefined) clearTimeout(debounceId);
    };
  }, []);

  useHeraldChatInstanceRemountEffect({
    conversationId,
    capturedConversationId,
    getTransportConversationId,
    messagesLength: messages.length,
    initialMessagesLength: initialMessages.length,
    setChatInstanceId,
  });

  useHeraldChatSidebarSync({
    conversationId,
    capturedConversationId,
    getTransportConversationId,
    getTransportConversationTitle,
    resetConversation,
    setConversationId,
    messages,
    status,
    registerConversationFromFirstStream,
    optimisticallyUpsertConversationAtTop,
  });

  // A reconnect between a drop and the resumed stream is still the same streaming turn.
  const isStreaming =
    status === "streaming" || status === "submitted" || isStreamReconnecting;
  const isResponsePending = isStreaming || isActiveResumeAccepted;
  // From a drop until the first resumed chunk the answer cannot grow; tell the user why it paused.
  const isStreamConnectionLost =
    streamReconnectReason === "stream_interrupted" && status !== "streaming";

  useEffect(() => {
    if (isStreaming) {
      setActiveResumeAttemptKey(null);
    }
  }, [activeResumeAttemptKey, isStreaming]);

  const setIsHeraldChatEmpty = useHeraldChatSettingsStore(
    (state) => state.setIsHeraldChatEmpty,
  );
  const setIsStreaming = useHeraldChatSettingsStore(
    (state) => state.setIsStreaming,
  );
  const setActiveConversationId = useHeraldChatSettingsStore(
    (state) => state.setActiveConversationId,
  );
  const isDeepAnalysisEnabled = useHeraldChatSettingsStore(
    (state) => state.isDeepAnalysisEnabled,
  );
  const toggleDeepAnalysis = useHeraldChatSettingsStore(
    (state) => state.toggleDeepAnalysis,
  );

  /** Keep the settings store in sync so the composer briefing menu can read streaming state. */
  useEffect(() => {
    setIsStreaming(isStreaming);
    return () => setIsStreaming(false);
  }, [isStreaming, setIsStreaming]);

  useLayoutEffect(() => {
    setActiveConversationId(
      conversationId ?? getTransportConversationId() ?? null,
    );
    return () => setActiveConversationId(null);
  }, [
    capturedConversationId,
    conversationId,
    getTransportConversationId,
    setActiveConversationId,
  ]);

  const hasVisibleMessages = messages.some(
    (message) => message.role === "user" || message.role === "assistant",
  );
  const isEmpty = !hasVisibleMessages;

  // useLayoutEffect syncs store before paint so isHeraldChatEmpty does not lag one frame and flash chrome when switching conversations.
  useLayoutEffect(() => {
    setIsHeraldChatEmpty(isEmpty);
    return () => setIsHeraldChatEmpty(false);
  }, [isEmpty]);

  const lastMessage = messages[messages.length - 1];
  const lastVisibleAssistantId = (() => {
    for (let index = messages.length - 1; index >= 0; index--) {
      const message = messages[index];
      if (message != null && message.role === "assistant") {
        return message.id;
      }
    }
    return null;
  })();
  const showPendingThinking =
    isResponsePending && lastMessage != null && lastMessage.role === "user";

  const showDevToolCalls = useHeraldDebugToolCallsEnabled();

  const streamingQuipMessage = useStreamingQuipForLatestAssistant({
    messages,
    lastVisibleAssistantId,
    isStreaming,
    showPendingThinking,
    showDevToolCalls,
  });

  useChatTiming({ status, messages, conversationId: capturedConversationId });

  const sendMessageForCurrentTurn = useCallback(
    (message: SendableMessage) => {
      resumedAssistantMessageIdRef.current = null;
      lastChatRequestTriggerRef.current = "submit";
      (sendMessage as (message: SendableMessage) => void)(message);
    },
    [sendMessage],
  );

  const { input, setInput, files, setFiles, inputRef, handleSubmit } =
    useChatSubmit({
      isStreaming: isResponsePending,
      sendMessage: sendMessageForCurrentTurn,
      setMessages,
      resetConversation,
    });

  useEffect(() => {
    if (status !== "error" || error == null) return;
    if (handledStreamErrorRef.current === error) return;
    handledStreamErrorRef.current = error;
    const streamFailure = getConversationStreamFailure();

    if (streamFailure === "conversation_run_active") {
      // naapi kept the running turn and did not save this submit: attach to the run, return the text to the composer.
      clearError();
      const unsentMessage = messages.at(-1);
      if (
        lastChatRequestTriggerRef.current === "submit" &&
        unsentMessage?.role === "user"
      ) {
        setMessages((currentMessages) =>
          currentMessages.at(-1)?.id === unsentMessage.id
            ? currentMessages.slice(0, -1)
            : currentMessages,
        );
        const unsentText = readMessageText(unsentMessage);
        // Keep anything typed since the submit, and never drop the unsent text.
        setInput((currentInput) =>
          [unsentText, currentInput].filter(Boolean).join("\n\n"),
        );
      }
      toast.info("A response is already running in this chat.");
      setStreamReconnectReason("conversation_run_active");
      const runningConversationId =
        conversationId ?? getTransportConversationId();
      void (async () => {
        // History holds the running turn's user message and pending assistant row; the replay fills that row.
        if (runningConversationId != null) {
          await reconcileConversationWithServerHistory(runningConversationId);
        }
        await reconnectConversationStream();
      })();
      return;
    }

    if (streamFailure !== "stream_interrupted") return;
    const lastEventId = getConversationStreamLastEventId();
    if (lastEventId !== streamReconnectLastEventIdRef.current) {
      // New events arrived since the previous attempt, so the backoff starts over.
      streamReconnectLastEventIdRef.current = lastEventId;
      streamReconnectAttemptRef.current = 0;
    }
    const attempt = streamReconnectAttemptRef.current;
    if (attempt >= STREAM_RECONNECT_MAX_ATTEMPTS) {
      // Give up and keep the error banner; Retry can still attach through the 409 path.
      setStreamReconnectReason(null);
      return;
    }
    streamReconnectAttemptRef.current = attempt + 1;
    clearError();
    setStreamReconnectReason("stream_interrupted");
    streamReconnectTimerRef.current = setTimeout(
      () => void reconnectConversationStream(),
      Math.min(
        STREAM_RECONNECT_BASE_DELAY_MS * 2 ** attempt,
        STREAM_RECONNECT_MAX_DELAY_MS,
      ),
    );
  }, [
    clearError,
    conversationId,
    error,
    getConversationStreamFailure,
    getConversationStreamLastEventId,
    getTransportConversationId,
    messages,
    reconcileConversationWithServerHistory,
    reconnectConversationStream,
    setInput,
    setMessages,
    status,
  ]);

  const { isRecording, isSupported, startListening, stopListening } =
    useVoiceToText({
      shouldAbort: isStreaming,
      onFinalPhrase: useCallback(
        (phrase: string) => {
          let truncated = false;
          setInput((prev) => {
            const result = appendDictatedPhrase(prev, phrase);
            truncated = result.truncated;
            return result.value;
          });
          if (truncated) notifyDictationTruncated();
        },
        [setInput],
      ),
      onError: useCallback((info: VoiceRecognitionErrorInfo) => {
        const msg = voiceRecognitionToastMessage(info);
        if (msg) toast.error(msg);
      }, []),
    });

  const handleStop = useCallback(async () => {
    if (isRecording) {
      stopListening();
      return;
    }

    cancelStreamReconnect();
    const activeConversationId = conversationId ?? getTransportConversationId();
    const assistantMessageId = getTransportAssistantMessageId();
    try {
      if (activeConversationId && assistantMessageId) {
        await stopConversationStream(activeConversationId, assistantMessageId);
      }
    } catch {
      toast.error("Couldn’t stop the response on the server.");
    } finally {
      disconnectConversationStream();
      await stop();
    }
  }, [
    cancelStreamReconnect,
    conversationId,
    disconnectConversationStream,
    getTransportAssistantMessageId,
    getTransportConversationId,
    isRecording,
    stopListening,
    stop,
  ]);

  const handleBannerRetry = useCallback(async () => {
    const assistantMessageId =
      resumedAssistantMessageIdRef.current ?? lastVisibleAssistantId;
    if (!assistantMessageId) return;

    // AI SDK regenerate requires the target message locally and removes this placeholder before sending.
    setMessages((currentMessages) =>
      currentMessages.some((message) => message.id === assistantMessageId)
        ? currentMessages
        : [
            ...currentMessages,
            { id: assistantMessageId, role: "assistant", parts: [] },
          ],
    );
    resumedAssistantMessageIdRef.current = null;
    lastChatRequestTriggerRef.current = "regenerate";

    clearError();
    try {
      await regenerate({ messageId: assistantMessageId });
    } catch (err) {
      console.error("[handleBannerRetry]", err);
    }
  }, [clearError, regenerate, lastVisibleAssistantId, setMessages]);

  /** Always pass `onMicPress` so the mic is not `disabled` when unsupported (disabled buttons skip hover). */
  const handleMicPress = useCallback(() => {
    if (!isSupported) {
      toast.info("Dictation is not supported in this browser.");
      return;
    }
    startListening();
  }, [isSupported, startListening]);

  const handleBriefingModeChange = useCallback(
    (mode: "briefing" | "deep") => {
      const wantDeep = mode === "deep";
      if (wantDeep !== isDeepAnalysisEnabled) toggleDeepAnalysis();
    },
    [isDeepAnalysisEnabled],
  );

  const {
    scrollRef,
    endRef,
    showScrollButton,
    scrollToBottom,
    messageListMinHeight,
  } = useChatScroll({
    isStreaming,
    messageCount: messages.length,
    conversationId,
    isScrollAreaMounted: !isEmpty,
    isStreamConnectionLost,
    latestAssistantTextLength:
      lastMessage?.role === "assistant"
        ? readMessageText(lastMessage).length
        : 0,
  });

  const chatInputProps = {
    ref: inputRef,
    value: input,
    onChange: setInput,
    onSubmit: handleSubmit,
    onStop: handleStop,
    isStreaming: isResponsePending,
    isRecording,
    onMicPress: handleMicPress,
    variant: isEmpty ? "landing" : "conversation",
    files,
    onFilesChange: setFiles,
    briefingMenu: {
      mode: isDeepAnalysisEnabled ? "deep" : "briefing",
      onModeChange: handleBriefingModeChange,
      isDisabled: isResponsePending,
    },
  } satisfies ChatInputProps;

  return (
    <ChatPromptSubmitContext.Provider value={{ submitPrompt: handleSubmit }}>
      <div className="flex h-full min-w-0 flex-col">
        {isEmpty ? (
          <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto overscroll-contain px-3 pb-8 pt-4 sm:px-6">
            <div className="m-auto flex w-full min-w-0 shrink-0 flex-col">
              <ChatEmptyState onSubmit={handleSubmit}>
                <ChatInput {...chatInputProps} />
              </ChatEmptyState>
            </div>
          </div>
        ) : (
          <>
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
              <div
                ref={scrollRef}
                className="min-h-0 min-w-0 w-full flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6"
              >
                <ChatErrorBoundary>
                  <HeraldChatMessageList
                    messages={messages}
                    isStreaming={isStreaming}
                    showPendingThinking={showPendingThinking}
                    conversationId={
                      conversationId ?? capturedConversationId ?? undefined
                    }
                    streamingQuipMessage={streamingQuipMessage}
                    // ThinkingBubble already shows while the last message is the user's.
                    isStreamConnectionLost={
                      isStreamConnectionLost && !showPendingThinking
                    }
                    className="mx-auto min-w-0 max-w-3xl space-y-6 pb-14 sm:min-w-75"
                    minHeight={messageListMinHeight}
                    endRef={endRef}
                  />
                </ChatErrorBoundary>
              </div>

              {showScrollButton ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center px-4 sm:px-6">
                  <div className="pointer-events-auto flex w-full max-w-3xl justify-center sm:min-w-75">
                    <IconButton
                      icon={
                        <ArrowDownIcon size={18} className="text-gray-600" />
                      }
                      variant="outline"
                      size="md"
                      onPress={scrollToBottom}
                      aria-label="Scroll to bottom"
                      tooltipLabel="Scroll to bottom"
                      className="size-8 shrink-0 rounded-full bg-white p-0 shadow-sm"
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <ChatErrorBanner
              key={error?.message}
              message={error?.message}
              onRetry={handleBannerRetry}
              isRetryDisabled={isResponsePending}
            />

            <ChatInput {...chatInputProps} />
          </>
        )}

        {isEmpty && (
          <ChatErrorBanner key={error?.message} message={error?.message} />
        )}
      </div>
    </ChatPromptSubmitContext.Provider>
  );
};

export default HeraldChat;
